import React from 'react';
import Link from 'next/link';
import { TypeIcon } from 'src/components';
import { getProfileUrl } from 'src/utils/profileUrl';
import { formatSocClassification } from 'src/components/Profiles/components/ProfileDetails/ProfileSiteOfCareDetails';

interface SearchResultItem {
  id: string | number;
  type: string;
  provider_id: string | number;
  name: string;
  city?: string;
  state?: string;
  specialty: string;
  soc_type?: string;
  soc_subtype?: string;
}

interface GlobalSearchItemProps {
  item: SearchResultItem;
  onClick?: () => void;
}

const GlobalSearchItem: React.FC<GlobalSearchItemProps> = ({
  item,
  onClick,
}): React.ReactElement => {
  const hasLocation = item.city && item.state;
  const displaySpecialty = item.specialty || formatSocClassification(item);

  return (
    <Link
      href={getProfileUrl(item.type, item.provider_id)}
      className="no-underline"
      data-testid={`result-link-${item.id}`}
      scroll={false}
      onClick={onClick}
    >
      <div
        data-testid="search-result-item"
        className="w-full px-4 py-3 bg-background-body rounded-xl flex flex-col justify-center items-start gap-3 overflow-hidden hover:bg-neutral-50 cursor-pointer"
      >
        <div className="self-stretch flex flex-col justify-start items-start gap-0.5">
          <div className="self-stretch h-6 inline-flex justify-between items-center">
            <div className="flex justify-start items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <TypeIcon
                  type={item.soc_type || item.type}
                  style={{ color: 'rgb(107 114 128)' }}
                  data-testid={`type-icon-${item.id}`}
                />
              </div>
              <div
                className="justify-center"
                data-testid={`result-name-${item.id}`}
                dangerouslySetInnerHTML={{
                  __html: `<span class="text-sky-900 text-base font-bold font-['Inter'] underline leading-6 line-clamp-1">${item.name}</span>`,
                }}
              />
            </div>
          </div>
          <div className="self-stretch inline-flex justify-start items-center gap-1.5">
            <div className="flex justify-start items-center gap-1">
              <div
                className="justify-start text-tertiary text-sm font-medium font-['Inter'] leading-5"
                data-testid={`result-specialty-${item.id}`}
                dangerouslySetInnerHTML={{
                  __html: displaySpecialty,
                }}
              />
            </div>
            {hasLocation && (
              <>
                <div className="w-1.5 h-1.5 bg-zinc-300 rounded-full" />
                <div
                  className="justify-start text-tertiary text-sm font-medium font-['Inter'] leading-5"
                  data-testid={`result-location-${item.id}`}
                  dangerouslySetInnerHTML={{
                    __html: `${item.city}, ${item.state}`,
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default GlobalSearchItem;
